/**
 * Ordering, search and hover text for redaction reason pickers.
 *
 * Every picker lists reasons A–Z by title (case-insensitive, numbers compared
 * by value, so "RCW 5.60" sorts before "RCW 42.56"), shows the title only, and
 * keeps the citation and full legal text for the hover.
 */
import type { RedactionReason } from '../../types';

type ReasonText = Pick<RedactionReason, 'title' | 'description' | 'citation' | 'legal_text'>;

const titleCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** A–Z by title, case-insensitive with numeric-aware comparison. */
export function compareReasonTitles(a: Pick<RedactionReason, 'title'>, b: Pick<RedactionReason, 'title'>): number {
  return titleCollator.compare(a.title.trim(), b.title.trim());
}

/** A sorted copy; the input (often React state) is left untouched. */
export function sortReasonsByTitle<T extends Pick<RedactionReason, 'title'>>(reasons: readonly T[]): T[] {
  return [...reasons].sort(compareReasonTitles);
}

/** Reasons whose title, description, citation or legal text contains `query` (case-insensitive). */
export function filterReasons<T extends ReasonText>(reasons: readonly T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...reasons];
  return reasons.filter((r) =>
    [r.title, r.description, r.citation, r.legal_text].some((field) => !!field && field.toLowerCase().includes(q)),
  );
}

export interface ReasonDetails {
  citation: string;
  /** Legal text first, then the description when it adds something. */
  paragraphs: string[];
}

/** What the hover over a reason shows, or null when the reason has nothing beyond its title. */
export function reasonDetails(reason: ReasonText): ReasonDetails | null {
  const citation = reason.citation?.trim() ?? '';
  const legalText = reason.legal_text?.trim() ?? '';
  const description = reason.description?.trim() ?? '';
  const paragraphs = [legalText, description !== legalText ? description : ''].filter(Boolean);
  if (!citation && paragraphs.length === 0) return null;
  return { citation, paragraphs };
}

/** Whether a reason already has this title (trimmed, case-insensitive): pickers select reasons by title. */
export function hasReasonTitle(reasons: readonly Pick<RedactionReason, 'title'>[], title: string): boolean {
  const wanted = { title };
  return reasons.some((r) => compareReasonTitles(r, wanted) === 0);
}

/**
 * The title a picker offers to create from its search, or null when the search
 * is empty or a reason with that exact title already exists (FB-1013).
 */
export function reasonTitleToCreate(reasons: readonly Pick<RedactionReason, 'title'>[], query: string): string | null {
  const title = query.trim();
  if (!title || hasReasonTitle(reasons, title)) return null;
  return title;
}
